// Registry Central (Registry Management) — every proposed master entity in
// one table: create one entry, or bulk-upload an Excel sheet of them.
// Action ⋯ menu per status — Pending Validation: View / Validate / Reject; a validated Market
// goes to Pending Completion until Market Registry captures the rest, then
// it is Active. Markets created directly in Market Registry are listed too
// (view-only here). Archived → "Restore"; other rows get the ⋯ menu.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { marketsApi } from '../../../../api/marketsApi';
import NlimsDataTable from '../../../shared/NlimsDataTable/NlimsDataTable';
import { useAnchoredMenu, anchorRect } from '../../../shared/useAnchoredMenu';
import { useCollectionSync } from '../../../../hooks/useCollectionSync';
import { useLocationOptions } from '../../shared/useLocationOptions';
import { Select } from '../../../reusable/formRecords/formFieldPrimitives';
import {
  COMPLETION_TYPES, REGISTRY_STATUS_OPTIONS, REGISTRY_TYPE_OPTIONS, registryCentralApi, registryStatusLabel, registryTypeLabel,
} from '../../../../api/registryCentralApi';
import { BulkUploadModal, ConfirmModal, CreateRegistryModal, RegistryDetailsModal } from './RegistryCentralModals';

const STATUS_STYLE = {
  active:   'color:#15803d;background:#f0fdf4;border:1px solid #bbf7d0',
  pending_validation: 'color:#4b5563;background:#f3f4f6;border:1px solid #e5e7eb',
  pending_completion: 'color:#1d4ed8;background:#eff6ff;border:1px solid #bfdbfe',
  rejected: 'color:#991b1b;background:#fef2f2;border:1px solid #fca5a5',
  archived: 'color:#b45309;background:#fffbeb;border:1px solid #fde68a',
  inactive: 'color:#dc2626;background:#fef2f2;border:1px solid #fecaca',
};
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatDate(iso) {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return '';
  return `${String(d.getDate()).padStart(2, '0')}-${MONTHS[d.getMonth()]}-${d.getFullYear()}`;
}

const statusBadge = (value, type, row) => (type !== 'display' ? value
  : `<span style="${STATUS_STYLE[row.status] ?? STATUS_STYLE.pending_validation};font-size:11px;font-weight:600;padding:2px 9px;border-radius:999px;white-space:nowrap">${value}</span>`);

// Every row gets the ⋯ button; the menu lists the actions for its status.
const actionCell = (_d, type) => (type !== 'display' ? ''
  : '<button class="nlims-action-btn" data-action="menu" title="Actions">&#8943;</button>');

const COLUMNS = [
  { title: 'Registry Name', data: 'name',        sortable: true },
  { title: 'Registry Type', data: 'typeLabel',   sortable: true },
  { title: 'State',         data: 'state',       sortable: true },
  { title: 'LGA',           data: 'lga',         sortable: true },
  { title: 'Status',        data: 'statusLabel', sortable: true, render: statusBadge },
  { title: 'Date Created',  data: 'dateLabel',   sortable: true, render: (v, type, row) => (type === 'sort' ? row.created_at : v) },
  { title: 'Action',        data: null,          sortable: false, render: actionCell },
];

function RowMenu({ rect, row, onClose, onPick }) {
  const { menuRef, style } = useAnchoredMenu(rect, onClose);
  const item = (key, label, cls = 'text-gray-700 hover:bg-gray-50') => (
    <button type="button" onClick={() => onPick(key)} className={`w-full px-4 py-2.5 text-left text-sm font-medium ${cls}`}>{label}</button>
  );
  return createPortal(
    <>
      <div className="fixed inset-0 z-[9998]" onClick={onClose} />
      <div ref={menuRef} role="menu" style={{ ...style, minWidth: 190 }} className="rounded-xl border border-gray-100 bg-white py-1.5 shadow-2xl">
        {item('view', 'View Details')}
        {row.isModuleRecord ? item('open', 'Open in Market Registry') : (
          <>
            {row.status === 'pending_validation' && item('validate', 'Validate', 'text-green-700 hover:bg-green-50')}
            {row.status === 'pending_validation' && item('reject', 'Reject', 'text-red-600 hover:bg-red-50')}
            {row.status === 'pending_completion' && item('complete', 'Complete in Market Registry', 'text-blue-700 hover:bg-blue-50')}
            {row.status === 'archived' && item('restore', 'Restore', 'text-green-700 hover:bg-green-50')}
            {row.linked_record_uuid && row.status !== 'pending_completion' && item('open', 'Open in Market Registry')}
            {!['archived', 'rejected'].includes(row.status) && item('edit', 'Edit')}
            {['active', 'inactive'].includes(row.status) && (
              <>
                <div className="mx-3 my-1 h-px bg-gray-100" />
                {row.status === 'active' ? item('deactivate', 'Deactivate', 'text-amber-700 hover:bg-amber-50') : item('activate', 'Activate', 'text-green-700 hover:bg-green-50')}
              </>
            )}
            {row.status !== 'archived' && item('archive', 'Archive', 'text-red-600 hover:bg-red-50')}
          </>
        )}
      </div>
    </>,
    document.body
  );
}

const CONFIRM = {
  validate:   { title: 'Validate Registry Entry?', label: 'Validate', message: (r) => (COMPLETION_TYPES.includes(r.registry_type)
    ? `${r.name} will be validated. Complete its details in Market Registry to make it Active.`
    : `${r.name} will be validated and become Active in the registry.`) },
  restore:    { status: 'active',   title: 'Restore Registry Entry?',  label: 'Restore',  message: (r) => `${r.name} will be restored as Active.` },
  archive:    { status: 'archived', title: 'Archive Registry Entry?',  label: 'Archive',  message: (r) => `${r.name} will be archived. You can restore it later.` },
  deactivate: { status: 'inactive', title: 'Deactivate Registry Entry?', label: 'Deactivate', message: (r) => `${r.name} will be marked Inactive.` },
  activate:   { status: 'active',   title: 'Activate Registry Entry?', label: 'Activate', message: (r) => `${r.name} will be marked Active.` },
};

// Market Registry records with no Registry Central entry (created directly in
// Core Module 2) — shown here view-only.
const marketToRow = (m) => ({
  uuid: `market:${m.uuid}`, market_uuid: m.uuid, isModuleRecord: true, source: 'module_registry',
  registry_type: 'market', name: m.market_name, state: m.state ?? '', lga: m.lga ?? '', ward: '',
  status: String(m.status ?? '').toLowerCase() === 'inactive' ? 'inactive' : 'active',
  created_at: m.created_at ?? '', created_by: '', linked_record_uuid: m.uuid,
});

export default function RegistryCentral() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [entries, setEntries] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState({ type: '', state: '', status: '' });
  const [modal, setModal] = useState(null); // { kind, row? }
  const [menu, setMenu] = useState(null);
  const [notice, setNotice] = useState('');
  const { stateOptions } = useLocationOptions('');

  const load = useCallback(async () => {
    const { items } = await registryCentralApi.listAll();
    setEntries(items);
  }, []);
  const loadMarkets = useCallback(async () => {
    try {
      const { items } = await marketsApi.listAll({ per_page: 1000 });
      setMarkets(items ?? []);
    } catch {
      setMarkets([]);
    }
  }, []);
  useEffect(() => { load(); loadMarkets(); }, [load, loadMarkets]);
  useCollectionSync(registryCentralApi.collectionKey, load);
  useCollectionSync(marketsApi.collectionKey, loadMarkets);

  // Opened from a "New Market Proposed" notification → show that entry.
  const focusUuid = searchParams.get('entry');
  useEffect(() => {
    if (!focusUuid) return;
    const row = entries.find((e) => e.uuid === focusUuid);
    if (!row) return;
    setModal({ kind: 'view', row });
    setSearchParams({}, { replace: true });
  }, [focusUuid, entries, setSearchParams]);

  const allEntries = useMemo(() => {
    const linked = new Set(entries.map((e) => e.linked_record_uuid).filter(Boolean));
    return [...entries, ...markets.filter((m) => m.uuid && !linked.has(m.uuid)).map(marketToRow)];
  }, [entries, markets]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allEntries
      .filter((e) => (!filters.type || e.registry_type === filters.type)
        && (!filters.state || e.state.toLowerCase() === filters.state.toLowerCase())
        && (!filters.status || e.status === filters.status))
      .filter((e) => !q || [e.name, e.state, e.lga, e.ward, registryTypeLabel(e.registry_type), e.batch_name]
        .some((v) => String(v ?? '').toLowerCase().includes(q)))
      .map((e) => ({ ...e, typeLabel: registryTypeLabel(e.registry_type), statusLabel: registryStatusLabel(e.status), dateLabel: formatDate(e.created_at) }));
  }, [allEntries, filters, query]);

  const openInMarketRegistry = useCallback((row) => {
    if (row.status === 'pending_completion') navigate('/markets-trade/new?subModule=registry', { state: { registryEntry: row } });
    else navigate(`/markets-trade/view/${row.linked_record_uuid}?subModule=registry`);
  }, [navigate]);

  const handleRowAction = useCallback((action, row, meta) => {
    if (action === 'menu') setMenu({ row, rect: anchorRect(meta?.button) });
  }, []);

  const validateEntry = async (row) => {
    const updated = await registryCentralApi.validate(row.uuid);
    setModal(null);
    setNotice(updated.status === 'pending_completion'
      ? `${row.name} validated — complete its details in Market Registry to make it Active.`
      : `${row.name} validated and Active.`);
    load();
  };
  const rejectEntry = async (row, notes) => {
    await registryCentralApi.reject(row.uuid, notes);
    setModal(null);
    setNotice(`${row.name} was rejected.`);
    load();
  };

  const pickMenu = (key) => {
    const row = menu.row;
    setMenu(null);
    if (key === 'complete' || key === 'open') openInMarketRegistry(row);
    else if (key === 'reject') setModal({ kind: 'view', row, rejecting: true });
    else setModal({ kind: key, row });
  };

  const applyStatus = async () => {
    const { kind, row } = modal;
    if (kind === 'validate') return validateEntry(row);
    await registryCentralApi.setStatus(row.uuid, CONFIRM[kind].status);
    setModal(null);
    load();
  };

  const setFilter = (key) => (e) => setFilters((prev) => ({ ...prev, [key]: e.target.value }));
  const filterCls = 'w-full appearance-none rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-green-400';

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[240px] flex-1">
          <svg className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <circle cx="11" cy="11" r="7" /><path strokeLinecap="round" d="M20 20l-3.5-3.5" />
          </svg>
          <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && setQuery(searchInput)}
            placeholder="Search here" className="w-full rounded-lg border border-gray-200 bg-white py-2.5 pl-9 pr-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-400" />
        </div>
        <button type="button" onClick={() => setQuery(searchInput)} className="h-10 rounded-lg bg-green-100 px-5 text-sm font-semibold text-green-700 hover:bg-green-200">Search</button>
        <div className="ml-auto flex gap-2">
          <button type="button" onClick={() => setModal({ kind: 'create' })} className="flex h-10 items-center gap-2 rounded-lg bg-green-600 px-5 text-sm font-semibold text-white hover:bg-green-700">
            <span className="text-base leading-none">+</span> Create Registry
          </button>
          <button type="button" onClick={() => setModal({ kind: 'bulk' })} className="h-10 rounded-lg bg-green-600 px-5 text-sm font-semibold text-white hover:bg-green-700">
            Bulk Upload
          </button>
        </div>
      </div>

      <div className="mb-5 grid grid-cols-1 gap-4 md:grid-cols-3">
        {[
          ['Registry Type', 'type', REGISTRY_TYPE_OPTIONS],
          ['State', 'state', stateOptions],
          ['Status', 'status', REGISTRY_STATUS_OPTIONS],
        ].map(([label, key, options]) => (
          <div key={key}>
            <label className="mb-1.5 block text-[13px] font-bold text-gray-900">{label}</label>
            <Select value={filters[key]} onChange={setFilter(key)} options={options} placeholder="All" className={filterCls} />
          </div>
        ))}
      </div>

      {notice && (
        <div className="mb-3 flex items-center justify-between rounded-lg border border-green-100 bg-green-50 px-4 py-2.5 text-sm text-green-700">
          <span>{notice}</span>
          <button type="button" onClick={() => setNotice('')} className="text-xs font-semibold">Dismiss</button>
        </div>
      )}

      <NlimsDataTable
        tableId="registry-central-table"
        columns={COLUMNS}
        data={rows}
        defaultSort={[5, 'desc']}
        hideSearch
        pageLength={25}
        enableRowSelection
        onRowAction={handleRowAction}
      />

      {menu && <RowMenu rect={menu.rect} row={menu.row} onClose={() => setMenu(null)} onPick={pickMenu} />}

      {modal?.kind === 'create' && <CreateRegistryModal onClose={() => setModal(null)} onSaved={() => { setNotice('Registry entry created. Markets go to Pending Completion — use Complete to fill in the rest in Market Registry.'); load(); }} />}
      {modal?.kind === 'edit' && <CreateRegistryModal entry={modal.row} onClose={() => setModal(null)} onSaved={load} />}
      {modal?.kind === 'bulk' && (
        <BulkUploadModal onClose={() => setModal(null)} onSaved={(count) => { setNotice(`${count} registry entr${count === 1 ? 'y' : 'ies'} uploaded.`); load(); }} />
      )}
      {modal?.kind === 'view' && (
        <RegistryDetailsModal
          entry={modal.row}
          initialRejecting={Boolean(modal.rejecting)}
          onClose={() => setModal(null)}
          onValidate={modal.row.isModuleRecord ? undefined : () => validateEntry(modal.row)}
          onReject={(notes) => rejectEntry(modal.row, notes)}
          onComplete={() => openInMarketRegistry(modal.row)}
          onOpenRecord={modal.row.linked_record_uuid ? () => openInMarketRegistry(modal.row) : undefined}
        />
      )}
      {modal && CONFIRM[modal.kind] && (
        <ConfirmModal
          title={CONFIRM[modal.kind].title}
          message={CONFIRM[modal.kind].message(modal.row)}
          confirmLabel={CONFIRM[modal.kind].label}
          onConfirm={applyStatus}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
